import { ACTIVE_COLOR, DEFAULT_COLOR, YELLOW_COLOR } from "@/constants/color";

import type React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Fab,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TrendingUp, BarChart3, Target, Award, Bot } from "lucide-react";
import Chart from "react-apexcharts";

import { FilterDrawer } from "@/components/forms/DateFilter";
import { Note } from "@/types/lead";
import { BOT_URL } from "@/constants/bot";
import { FilterIcon } from "lucide-react";

import { useAppointmentTable, useFunnelHealth } from "../services/useDashboard";
import { useMemo, useState } from "react";
import WeeklyReportDashboard from "@/components/charts/Weekly";
import last from "lodash/last";
import FunnelStackedChart from "@/components/charts/DetailFunnel";
import { useAuth } from "@/context/auth-context";
import { formatCurrency, formatWithCommas } from "@/utils/func";
import { useStats } from "@/services/dashboard/stats";
import FunnelChartExample from "@/components/charts/Funnel";
import { useTopDealsAndLeads } from "@/services/dashboard/top";
import HorizontalStackedBar from "@/components/charts/HorizontalStacked";

interface Props {
  startDate?: Date | null;
  endDate?: Date | null;
  userId?: string;
  userSelected?: string;
  projectSelected?: string;
  type?: string;
  region?: string;
  cordination?: string;
  principal?: string;
  master?: string;
}

export const FunnelHealthChart: React.FC<Props> = ({
  startDate,
  endDate,
  userId,
  userSelected,
  projectSelected,
  type,
  region,
  cordination,
  principal,
  master,
}) => {
  const filters = useMemo(
    () => ({
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
      master,
    }),
    [
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
      master,
    ]
  );

  const { data, isLoading, error, isError } = useFunnelHealth(filters);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.map((item) => ({
      ...item,
      shortName:
        item.statusName.length > 8
          ? item.statusName.substring(0, 8) + "..."
          : item.statusName,
    }));
  }, [data]);

  const metrics = useMemo(() => {
    if (!data || data.length === 0)
      return { totalReached: 0, totalWon: 0, overallConversionRate: 0 };

    let totalReached = 0;
    let totalWon = 0;

    for (const item of data) {
      totalReached += item.reached;
      totalWon += item.won;
    }

    const overallConversionRate =
      totalReached > 0 ? Math.round((totalWon / totalReached) * 100) : 0;

    return { totalReached, totalWon, overallConversionRate };
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            {error instanceof Error
              ? error.message
              : "Error al cargar los datos"}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const series = [
    {
      name: "Etapa alcanzada",
      data: chartData.map((item) => item.reached),
    },
  ];

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
      },
    },
    xaxis: {
      categories: chartData.map((item) => item.statusName),
      labels: { rotate: -45, style: { fontSize: "12px" } },
    },
    yaxis: {
      title: { text: "Número de tratos" },
    },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const item = chartData[dataPointIndex];
        if (dataPointIndex === 0) {
          return `
          <div style="padding:10px;">
            <strong>${item.statusName}</strong><br/>
            Etapa alcanzada: ${item.reached} tratos<br/>
          </div>
          `;
        }
        return `
          <div style="padding:10px;">
            <strong>${item.statusName}</strong><br/>
            Etapa alcanzada: ${item.reached} tratos<br/>
            Conversión: ${item.conversionRate}%
          </div>
        `;
      },
    },
    colors: ["#fbbf24"],
    grid: { strokeDashArray: 4 },
  };

  const contact = chartData.find((c) => c.statusName === "Primer Contacto");
  const signedContract = chartData.find(
    (c) => c.statusName === "Contrato Firmado"
  );

  return (
    <Card>
      <CardHeader
        title={
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <BarChart3 size={24} />
              <Typography variant="h6">Estado de salud del embudo</Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                icon={<Target size={14} />}
                label="CITAS SINOW"
                size="small"
                variant="outlined"
              />
              <Chip label="SEMANA PASADA" size="small" variant="outlined" />
              <Chip
                label="GANADO, PERDIDO, ABIERTO"
                size="small"
                variant="outlined"
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <Award size={20} />
              <Typography variant="h4">
                {`La tasa conversión a venta es ${
                  signedContract && contact
                    ? (
                        ((signedContract?.reached || 0) /
                          (contact?.reached || 0)) *
                        100
                      ).toFixed(2)
                    : 0
                }%`}
              </Typography>
            </Box>
            <Box display="flex" gap={3} mt={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp size={16} />
                <Typography variant="body2" color="text.secondary">
                  {metrics.totalReached} tratos totales
                </Typography>
              </Box>
            </Box>
          </Box>
        }
      />
      <CardContent>
        <Chart options={options} series={series} type="bar" height={400} />
      </CardContent>
    </Card>
  );
};

const AppointmentTable: React.FC<Props> = ({
  startDate,
  endDate,
  userId,
  userSelected,
  projectSelected,
  type,
  region,
  cordination,
  principal,
  master,
}) => {
  const filters = useMemo(
    () => ({
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
      master,
    }),
    [
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
      master,
    ]
  );
  const { data, isLoading, error, isError } = useAppointmentTable(filters);

  const chartData = useMemo(() => {
    return data || [];
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            {error instanceof Error
              ? error.message
              : "Error al cargar los datos"}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <DataGrid
      rows={chartData}
      rowCount={10}
      getRowId={(row) => row._id}
      columns={[
        {
          headerName: "Fecha de Cita",
          field: "appointmentDate",
          width: 300,
          valueGetter: (value: string) => {
            return dayjs(value).format("LL");
          },
        },
        {
          headerName: "Horario",
          field: "appointmentDatehour",
          width: 200,
          valueGetter: (_value: string, row: any) => {
            return dayjs(row.appointmentDate).format("hh:mm A");
          },
        },
        {
          headerName: "Asesor",
          field: "user",
          width: 400,
          valueGetter: (_value: string, row: any) => {
            if (!row.user) return "";
            return `${row.user?.name} ${row.user?.lastName}`;
          },
        },
        {
          headerName: "Nombre del prospecto",
          field: "fullName",
          width: 400,
        },
        {
          headerName: "Teléfono",
          field: "phone",
          width: 200,
        },
        {
          headerName: "Desarrollo",
          field: "campaign",
          width: 200,
        },
        {
          headerName: "Notas",
          field: "notes",
          width: 400,
          valueGetter: (_value: string, row: any) => {
            const note: Note | undefined = last(row?.notes || []);
            return note?.note || "";
          },
        },
      ]}
    />
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    startDate: undefined,
    endDate: undefined,
    projectSelected: "",
    userSelected: "",
    type: "",
    region: "",
    cordination: "",
    principal: "",
    master: "",
  });

  const { data } = useStats(filters);
  const { data: top } = useTopDealsAndLeads(filters);

  const handleResetFilters = () => {
    setFilters({
      startDate: undefined,
      endDate: undefined,
      projectSelected: "",
      userSelected: "",
      type: "",
      region: "",
      cordination: "",
      principal: "",
      master: "",
    });
  };

  const toggleFilterDrawer = () => {
    setFilterDrawerVisible((p) => !p);
  };

  const handleFilterChange = (key: string, value: string | Date | null) => {
    setFilters({
      ...filters,
      [key]: value,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          pb: { xs: "calc(64px + env(safe-area-inset-bottom))", sm: 0 },
          mt: 5,
        }}
      >
        <Box
          mb={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: DEFAULT_COLOR,
                display: { xs: "none", sm: "block" },
              }}
            >
              Tablero
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: DEFAULT_COLOR,
              }}
            >
              {`Bienvenido de nuevo ${user?.username
                ?.split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")}!`}
            </Typography>
          </Box>
        </Box>
        <Grid
          container
          spacing={2}
          sx={{
            mt: 1,
            mb: 5,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {[
            {
              number: formatWithCommas(data?.leads || 0),
              label: "Total de contactos",
            },
            {
              number: formatWithCommas(data?.deals || 0),
              label: "Total de contratos firmados",
            },
            {
              number: formatCurrency(data?.totalCash || 0),
              label: "Total en dinero",
            },
          ].map((item, index) => (
            <Grid
              size={{ xs: 12, sm: 4, md: 4 }}
              key={index}
              display="flex"
              justifyContent="center"
            >
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  maxWidth: 400,
                  height: 160,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    color: YELLOW_COLOR,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: {
                      xs: "2rem",
                      sm: "2.5rem",
                      md: "3rem",
                    },
                  }}
                >
                  {item.number}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 1,
                    color: ACTIVE_COLOR,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: {
                      xs: "0.8rem",
                      sm: "0.9rem",
                      md: "1rem",
                    },
                  }}
                >
                  {item.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ mt: 1, mb: 1 }}>
          <FunnelStackedChart
            startDate={filters.startDate}
            endDate={filters.endDate}
            userSelected={filters.userSelected}
            projectSelected={filters.projectSelected}
            type={filters.type}
            region={filters.region}
            cordination={filters.cordination}
            principal={filters.principal}
            master={filters.master}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 1, mb: 1 }}>
          <FunnelHealthChart
            startDate={filters.startDate}
            endDate={filters.endDate}
            userSelected={filters.userSelected}
            projectSelected={filters.projectSelected}
            type={filters.type}
            region={filters.region}
            cordination={filters.cordination}
            principal={filters.principal}
            master={filters.master}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 1, mb: 1 }}>
          <FunnelChartExample
            startDate={filters.startDate}
            endDate={filters.endDate}
            userSelected={filters.userSelected}
            projectSelected={filters.projectSelected}
            type={filters.type}
            region={filters.region}
            cordination={filters.cordination}
            principal={filters.principal}
            master={filters.master}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 1, mb: 1 }}>
          <HorizontalStackedBar data={top} />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 1, mb: 1 }}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <AppointmentTable
              startDate={filters.startDate}
              endDate={filters.endDate}
              userSelected={filters.userSelected}
              projectSelected={filters.projectSelected}
              type={filters.type}
              region={filters.region}
              cordination={filters.cordination}
              principal={filters.principal}
              master={filters.master}
            />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 1, mb: 1 }}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <WeeklyReportDashboard
              startDate={filters.startDate}
              endDate={filters.endDate}
              userSelected={filters.userSelected}
              projectSelected={filters.projectSelected}
              type={filters.type}
              region={filters.region}
              cordination={filters.cordination}
              principal={filters.principal}
              master={filters.master}
            />
          </Paper>
        </Grid>
      </Box>
      <Fab
        aria-label="add"
        sx={{
          backgroundColor: YELLOW_COLOR,
          color: "white",
          position: "fixed",
          bottom: `calc(env(safe-area-inset-bottom, 16px) + 150px)`,
          right: 16,
          zIndex: 3000,
          display: filterDrawerVisible ? "none" : "",
          "&:hover": {
            backgroundColor: YELLOW_COLOR,
          },
        }}
        onClick={toggleFilterDrawer}
      >
        <FilterIcon />
      </Fab>
      <Fab
        aria-label="bot"
        sx={{
          backgroundColor: DEFAULT_COLOR,
          color: "white",
          position: "fixed",
          bottom: `calc(env(safe-area-inset-bottom, 16px) + 80px)`,
          right: 16,
          zIndex: 3000,
          display: filterDrawerVisible ? "none" : "",
          "&:hover": {
            backgroundColor: DEFAULT_COLOR,
          },
        }}
        onClick={() => window.open(BOT_URL, "_blank")}
      >
        <Bot />
      </Fab>
      <FilterDrawer
        open={filterDrawerVisible}
        onClose={toggleFilterDrawer}
        filterChange={handleFilterChange}
        filters={filters}
        handleResetFilters={handleResetFilters}
        showUsers
        showProject
      />
    </LocalizationProvider>
  );
}
